// Atlas Shared Utilities — Time conversion and formatting
// All timestamps are stored as UTC (ISO 8601) and displayed as IST.

import { FOUNDER_TIMEZONE } from "../constants/index";

/**
 * Converts a UTC ISO string to a formatted IST string for display.
 * Example: "2026-08-06T03:44:00Z" → "Aug 6, 2026, 9:14 AM IST"
 */
export function formatIst(utcIso: string | null): string {
  if (!utcIso) return "—";
  try {
    const date = new Date(utcIso);
    return date.toLocaleString("en-IN", {
      timeZone: FOUNDER_TIMEZONE,
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZoneName: "short",
    });
  } catch {
    return utcIso;
  }
}

/**
 * Converts a UTC ISO string to a short formatted IST string (date only).
 * Example: "2026-08-06T03:44:00Z" → "Aug 6, 2026"
 */
export function formatIstDate(utcIso: string | null): string {
  if (!utcIso) return "—";
  try {
    const date = new Date(utcIso);
    return date.toLocaleDateString("en-IN", {
      timeZone: FOUNDER_TIMEZONE,
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return utcIso;
  }
}

/**
 * Converts a UTC ISO string to a time-only IST string.
 * Example: "2026-08-06T03:44:00Z" → "9:14 AM IST"
 */
export function formatIstTime(utcIso: string | null): string {
  if (!utcIso) return "—";
  try {
    const date = new Date(utcIso);
    return date.toLocaleTimeString("en-IN", {
      timeZone: FOUNDER_TIMEZONE,
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZoneName: "short",
    });
  } catch {
    return utcIso;
  }
}

/**
 * Returns the current time as a UTC ISO 8601 string.
 */
export function nowUtc(): string {
  return new Date().toISOString();
}

/**
 * Calculates the duration in minutes between two UTC timestamps.
 */
export function diffMinutes(fromUtc: string, toUtc: string): number {
  const from = new Date(fromUtc).getTime();
  const to = new Date(toUtc).getTime();
  return Math.round((to - from) / (1000 * 60));
}

/**
 * Formats a duration in minutes as a human-readable string.
 * Example: 150 → "2 hours 30 minutes"
 */
export function formatDuration(minutes: number | null): string {
  if (minutes === null) return "—";
  if (minutes < 1) return "less than a minute";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"}`;

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours < 24) {
    if (mins === 0) return `${hours} hour${hours === 1 ? "" : "s"}`;
    return `${hours} hour${hours === 1 ? "" : "s"} ${mins} minute${mins === 1 ? "" : "s"}`;
  }

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;

  if (remainingHours === 0) return `${days} day${days === 1 ? "" : "s"}`;
  return `${days} day${days === 1 ? "" : "s"} ${remainingHours} hour${remainingHours === 1 ? "" : "s"}`;
}

/**
 * Converts an IST "HH:MM" string to a cron-compatible UTC time.
 * IST is UTC+5:30, so we subtract 5 hours 30 minutes.
 * Returns { hour, minute } in UTC.
 */
export function istTimeToUtcCron(istTime: string): { hour: number; minute: number } {
  const [istHour, istMinute] = istTime.split(":").map(Number);
  let utcMinute = istMinute - 30;
  let utcHour = istHour - 5;

  if (utcMinute < 0) {
    utcMinute += 60;
    utcHour -= 1;
  }
  if (utcHour < 0) {
    utcHour += 24;
  }

  return { hour: utcHour, minute: utcMinute };
}
